import React from 'react';
import { FeatureAccessGate, useViewerFeature } from '@hopdrive/feature-portal-products';
import { useCustomerConfigLoaded, useUser } from '@store';

interface ProtectedRouteProps {
  /** The page; absent for a path that only ever renders the rooftop gate's verdict. */
  children?: JSX.Element;
  /** Feature name(s) the route requires. Absent, only a session is required. */
  requiredFeature?: string | string[];
  /**
   * The rooftop has this page. Set, the route renders RESTRICTED ACCESS when it is false (once the
   * rooftop's config and products have settled), before the feature check. Unset, the route is not
   * rooftop-gated.
   */
  available?: boolean;
}

/**
 * The embed's notice for a request with no session. The embed never redirects to a login page —
 * there is none; a hash in the query string is the only way in.
 */
export const AuthRequiredNotice: React.FC = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: '20px',
    textAlign: 'center',
    backgroundColor: '#f5f5f5',
  }}>
    <h1 style={{ color: '#333', marginBottom: '20px' }}>Authentication Required</h1>
    <p style={{ color: '#666', maxWidth: '600px', lineHeight: '1.6' }}>
      This portal requires a valid authentication link. Please check your email for a secure access link,
      or contact your Hopdrive administrator for assistance.
    </p>
    <p style={{ color: '#999', marginTop: '20px', fontSize: '14px' }}>
      Access links contain a secure hash parameter for authentication.
    </p>
  </div>
);

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredFeature, available }) => {
  const user = useUser();
  const customerConfigLoaded = useCustomerConfigLoaded();
  // Unconditionally, before any early return: hooks may not sit behind a branch. The grant set is
  // a constant under the hash session, so this never denies in practice (decision A6) — it is here
  // so the embed's route table reads like the dealer's.
  const features = requiredFeature === undefined ? [] : Array.isArray(requiredFeature) ? requiredFeature : [requiredFeature];
  const granted = useViewerFeature(...features);

  if (!user) return <AuthRequiredNotice />;
  // The embed has no PermissionDeniedPage; an ungranted feature reads as "no way in from here".
  const viewerPage = features.length === 0 || granted ? (children ?? null) : <AuthRequiredNotice />;
  // The rooftop's verdict wraps the feature check.
  if (available === undefined) return viewerPage;
  return (
    <FeatureAccessGate available={available} hostReady={customerConfigLoaded}>
      {viewerPage}
    </FeatureAccessGate>
  );
};

export default ProtectedRoute;
